import React from 'react';
import { Button } from './ui/button';
import { ArrowRight, Sparkles } from 'lucide-react';
import { useSettings } from '../contexts/SettingsContext';

export const Hero = () => {
  const { settings } = useSettings();
  const s = settings || {};

  const scrollToContact = () => {
    document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' });
  };

  const scrollToBrands = () => {
    document.getElementById('brands')?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <section className="relative min-h-screen pt-24 pb-12 overflow-hidden bg-gradient-to-b from-[#fefaf5] via-[#fef3ec] to-orange-100">
      {/* Subtle grid pattern */}
      <div
        className="absolute inset-0 opacity-[0.04]"
        style={{
          backgroundImage:
            'linear-gradient(to right, #000 1px, transparent 1px), linear-gradient(to bottom, #000 1px, transparent 1px)',
          backgroundSize: '60px 60px',
        }}
      />

      {/* Decorative blurred orbs - asymmetric placement */}
      <div className="absolute -top-32 -right-32 w-[500px] h-[500px] bg-orange-300 rounded-full mix-blend-multiply filter blur-3xl opacity-30 animate-pulse"></div>
      <div className="absolute top-1/3 -left-32 w-[400px] h-[400px] bg-rose-200 rounded-full mix-blend-multiply filter blur-3xl opacity-40 animate-pulse" style={{ animationDelay: '1.5s' }}></div>

      <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <div className="grid lg:grid-cols-12 gap-8 lg:gap-12 items-center min-h-[calc(100vh-12rem)]">
          {/* LEFT — Content (spans 7) */}
          <div className="lg:col-span-7 space-y-8">
            {/* Pill badge */}
            <div className="inline-flex items-center gap-2 bg-white border border-orange-200 px-4 py-2 rounded-full shadow-sm">
              <Sparkles size={14} className="text-orange-600" />
              <span className="text-xs font-bold tracking-wider text-gray-700 uppercase">
                {s.hero_eyebrow || 'Talent · Influence · Growth'}
              </span>
            </div>

            {/* Main heading — editorial style with mixed weights & italic */}
            <h1 className="text-5xl sm:text-6xl lg:text-7xl xl:text-8xl font-bold text-gray-900 leading-[1.05] tracking-tight">
              {s.hero_headline_line1 || 'Where'}{' '}
              <span className="italic font-serif text-orange-600">
                {s.hero_headline_italic || 'creators'}
              </span>
              <br />
              <span className="relative inline-block">
                <span className="relative z-10">{s.hero_headline_line2 || 'meet ambition.'}</span>
                <span className="absolute bottom-2 left-0 w-full h-3 bg-orange-200/60 -z-0"></span>
              </span>
            </h1>

            <p className="text-lg sm:text-xl text-gray-600 max-w-2xl leading-relaxed">
              {s.hero_subheading || "We connect bold brands with India's most influential voices. From 1,000+ creators to exclusive talent management — we make every campaign count."}
            </p>

            {/* CTAs */}
            <div className="flex flex-col sm:flex-row items-start gap-3 pt-2">
              <Button
                onClick={scrollToContact}
                size="lg"
                data-testid="hero-cta-primary"
                className="bg-gray-900 hover:bg-gray-800 text-white px-8 py-7 text-base font-semibold rounded-full transition-all duration-300 hover:scale-105 group"
              >
                {s.hero_cta_primary || 'Start a campaign'}
                <ArrowRight className="ml-2 group-hover:translate-x-1 transition-transform" size={18} />
              </Button>
              <Button
                onClick={scrollToBrands}
                size="lg"
                variant="ghost"
                data-testid="hero-cta-secondary"
                className="text-gray-900 hover:bg-gray-100 px-8 py-7 text-base font-semibold rounded-full"
              >
                {s.hero_cta_secondary || 'See our brands'}
              </Button>
            </div>

            {/* Inline stats strip */}
            <div className="flex flex-wrap gap-x-10 gap-y-4 pt-8 border-t border-gray-200">
              <div>
                <div className="text-3xl font-bold text-gray-900">1,000<span className="text-orange-600">+</span></div>
                <div className="text-sm text-gray-500 font-medium">Creators in network</div>
              </div>
              <div>
                <div className="text-3xl font-bold text-gray-900">50<span className="text-orange-600">+</span></div>
                <div className="text-sm text-gray-500 font-medium">Brand partnerships</div>
              </div>
              <div>
                <div className="text-3xl font-bold text-gray-900">200<span className="text-orange-600">+</span></div>
                <div className="text-sm text-gray-500 font-medium">Campaigns delivered</div>
              </div>
            </div>
          </div>

          {/* RIGHT — Visual collage (spans 5) */}
          <div className="lg:col-span-5 relative hidden lg:block">
            <div className="relative h-[600px]">
              {/* Floating card - Highlight stat */}
              <div className="absolute top-1/2 right-8 -translate-y-1/2 w-72 bg-gradient-to-br from-orange-500 to-rose-600 text-white rounded-2xl shadow-2xl p-8 rotate-2 hover:rotate-0 transition-transform duration-500">
                <div className="text-6xl font-bold mb-2">98%</div>
                <div className="text-base font-semibold opacity-90 mb-1">Brand satisfaction</div>
                <div className="text-sm opacity-75">Across 200+ campaigns</div>
              </div>

              {/* Floating sparkle accents */}
              <div className="absolute top-1/4 left-20 w-4 h-4 bg-orange-500 rotate-45"></div>
              <div className="absolute bottom-1/3 right-32 w-3 h-3 bg-rose-500 rounded-full"></div>
              <div className="absolute top-3/4 left-1/3 w-2 h-2 bg-amber-400 rotate-45"></div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
